import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CatalogSync,
  type BottleCategory,
  type CatalogSyncCandidate,
  type CatalogSyncSelection,
  type CatalogSyncSource,
} from '@/lib/api/v1';
import { Badge, Button, Card, EmptyState, Icon, Input, Skeleton, Textarea } from '@/design-system';
import { useToasts } from '@/lib/store';

const CATEGORY_OPTIONS: { id: BottleCategory; label: string; icon: 'bottle' | 'spirits' | 'beer' }[] = [
  { id: 'wine', label: 'Vin', icon: 'bottle' },
  { id: 'spirits', label: 'Brennevin', icon: 'spirits' },
  { id: 'beer', label: 'Øl', icon: 'beer' },
];

const DEFAULT_QUERIES = [
  'champagne',
  'prosecco',
  'riesling',
  'chablis',
  'sancerre',
  'bordeaux',
  'barolo',
  'amarone',
  'bag in box',
  'akevitt',
  'whisky',
  'gin',
  'cognac',
].join('\n');

export function AdminSyncPage() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [source, setSource] = useState<CatalogSyncSource>('hybrid');
  const [selection, setSelection] = useState<CatalogSyncSelection>('norway-common');
  const [categories, setCategories] = useState<BottleCategory[]>(['wine', 'spirits']);
  const [queriesText, setQueriesText] = useState(DEFAULT_QUERIES);
  const [requestBudget, setRequestBudget] = useState(100);
  const [limitPerQuery, setLimitPerQuery] = useState(5);
  const [lwinOffset, setLwinOffset] = useState(0);
  const [lwinLimit, setLwinLimit] = useState(50);
  const [backfillDays, setBackfillDays] = useState(30);
  const [runNow, setRunNow] = useState(false);

  const queries = useMemo(
    () => queriesText.split(/\n|,/).map((q) => q.trim()).filter(Boolean),
    [queriesText],
  );

  const previewQ = useQuery({
    queryKey: ['admin.catalog-sync.preview', source, selection, categories.join(','), queries.join('|'), lwinOffset, lwinLimit],
    queryFn: () => CatalogSync.preview({
      source,
      selection,
      categories,
      queries,
      limit: 80,
      lwinLimit,
      lwinOffset,
    }),
  });

  const start = useMutation({
    mutationFn: () => CatalogSync.start({
      source,
      selection,
      categories,
      queries,
      requestBudget,
      limitPerQuery,
      planLimit: 100,
      lwinLimit,
      lwinOffset,
      includeDetails: true,
      runNow,
    }),
    onSuccess: (job) => {
      push({ title: runNow ? 'Sync kjørt' : 'Sync lagt i kø', description: job.id, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['admin.jobs'] });
      void previewQ.refetch();
    },
    onError: (err: Error) => push({ title: 'Kunne ikke starte sync', description: err.message, tone: 'danger' }),
  });

  const backfill = useMutation({
    mutationFn: () => CatalogSync.backfill({
      source: source === 'vinmonopolet' ? 'hybrid' : source,
      categories: categories.includes('wine') ? ['wine'] : categories,
      startOffset: lwinOffset,
      rowsPerDay: lwinLimit,
      days: backfillDays,
      requestBudget,
      limitPerQuery: 1,
      runFirstNow: runNow,
    }),
    onSuccess: (result) => {
      push({
        title: 'Backfill planlagt',
        description: `${result.jobs.length} jobber · neste offset ${result.nextOffset}`,
        tone: 'success',
      });
      void qc.invalidateQueries({ queryKey: ['admin.jobs'] });
      setLwinOffset(result.nextOffset);
    },
    onError: (err: Error) => push({ title: 'Kunne ikke planlegge backfill', description: err.message, tone: 'danger' }),
  });

  const candidates = previewQ.data?.candidates ?? [];
  const wineApi = previewQ.data?.policy.wineApi;
  const lwin = previewQ.data?.policy.lwinDatabase;
  const lwinTotalRows = previewQ.data?.policy.lwinTotalRows;

  return (
    <div className="grid max-w-6xl gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle">
            <Icon name="chevron-left" size={14} /> Tilbake til admin
          </Link>
          <h1 className="ui-section-title text-3xl">Katalogsync</h1>
          <p className="text-sm text-mist">Velg kilder, kategorier og kandidatgrunnlag før jobben kjøres.</p>
        </div>
        <Button iconLeft="refresh" loading={start.isPending} onClick={() => start.mutate()}>
          {runNow ? 'Kjør sync' : 'Legg i kø'}
        </Button>
      </header>

      <section className="grid gap-4 xl:grid-cols-[22rem_1fr]">
        <div className="grid gap-4">
          <Card className="grid gap-4 p-5">
            <h2 className="ui-section-title text-xl">Policy</h2>
            <Segmented
              label="Kilde"
              value={source}
              options={[
                ['hybrid', 'Hybrid'],
                ['vinmonopolet', 'Vinmonopolet'],
                ['wineapi', 'WineAPI'],
                ['lwin', 'LWIN'],
              ]}
              onChange={(value) => setSource(value as CatalogSyncSource)}
            />
            <Segmented
              label="Utvalg"
              value={selection}
              options={[
                ['norway-common', 'Vanlig i Norge'],
                ['mixed', 'Alt relevant'],
                ['activity', 'Aktivitet'],
                ['queries', 'Søk'],
                ['lwin', 'LWIN-fil'],
              ]}
              onChange={(value) => setSelection(value as CatalogSyncSelection)}
            />
            <div className="grid gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-mist">Kategorier</span>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id, categories, setCategories)}
                    className={`ui-pill ${categories.includes(cat.id) ? 'ui-pill--active' : ''}`}
                  >
                    <Icon name={cat.icon} size={12} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="WineAPI budsjett"
                type="number"
                min={1}
                max={10000}
                value={requestBudget}
                onChange={(e) => setRequestBudget(Number(e.target.value))}
              />
              <Input
                label="Treff per søk"
                type="number"
                min={1}
                max={100}
                value={limitPerQuery}
                onChange={(e) => setLimitPerQuery(Number(e.target.value))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="LWIN start"
                type="number"
                min={0}
                value={lwinOffset}
                onChange={(e) => setLwinOffset(Number(e.target.value))}
              />
              <Input
                label="LWIN rader"
                type="number"
                min={1}
                max={500}
                value={lwinLimit}
                onChange={(e) => setLwinLimit(Number(e.target.value))}
              />
            </div>
            <Input
              label="Backfill-dager"
              type="number"
              min={1}
              max={365}
              value={backfillDays}
              onChange={(e) => setBackfillDays(Number(e.target.value))}
            />
            <Button
              variant="ghost"
              iconLeft="calendar"
              loading={backfill.isPending}
              onClick={() => backfill.mutate()}
              disabled={!lwin?.exists}
            >
              Planlegg LWIN-backfill
            </Button>
            <label className="flex items-center gap-2 text-sm text-parchment">
              <input
                type="checkbox"
                checked={runNow}
                onChange={(e) => setRunNow(e.target.checked)}
                className="h-4 w-4 accent-[color:var(--ui-color-candle)]"
              />
              Kjør direkte i stedet for kø
            </label>
          </Card>

          <Card className="grid gap-3 p-5">
            <h2 className="ui-section-title text-xl">Søk</h2>
            <Textarea
              value={queriesText}
              onChange={(e) => setQueriesText(e.target.value)}
              className="min-h-[12rem] font-mono text-xs"
              aria-label="Synkroniseringssøk"
            />
          </Card>
        </div>

        <div className="grid gap-4">
          <StatusStrip
            wineApiEnabled={Boolean(wineApi?.enabled)}
            wineApiLimit={wineApi?.dailyLimit}
            lwinExists={Boolean(lwin?.exists)}
            lwinPath={lwin?.configuredPath}
            lwinTotalRows={lwinTotalRows}
          />
          <Card className="grid gap-3 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="ui-section-title text-xl">Kandidater</h2>
              <Badge tone="mist">{candidates.length}</Badge>
              <Button className="ml-auto" variant="ghost" size="sm" iconLeft="refresh" onClick={() => previewQ.refetch()} loading={previewQ.isFetching}>
                Oppdater
              </Button>
            </div>
            {previewQ.isLoading ? (
              <div className="grid gap-2">
                <Skeleton height={52} /><Skeleton height={52} /><Skeleton height={52} />
              </div>
            ) : candidates.length === 0 ? (
              <EmptyState icon="container" title="Ingen kandidater" description="Endre utvalg eller legg inn søk." />
            ) : (
              <ul className="grid gap-2">
                {candidates.map((candidate) => (
                  <CandidateRow key={`${candidate.category}-${candidate.query}-${candidate.reason}`} candidate={candidate} />
                ))}
              </ul>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <span className="text-xs font-medium uppercase tracking-wider text-mist">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map(([id, text]) => (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`ui-pill ${value === id ? 'ui-pill--active' : ''}`}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusStrip({
  wineApiEnabled,
  wineApiLimit,
  lwinExists,
  lwinPath,
  lwinTotalRows,
}: {
  wineApiEnabled: boolean;
  wineApiLimit?: number;
  lwinExists: boolean;
  lwinPath?: string;
  lwinTotalRows?: number;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Card className="flex items-center gap-3 p-4">
        <Icon name="globe" className="text-candle" />
        <div className="grid">
          <span className="text-sm text-parchment">WineAPI</span>
          <span className="text-xs text-mist">{wineApiEnabled ? `${wineApiLimit ?? 100} requests/dag` : 'mangler API-nøkkel'}</span>
        </div>
        <Badge className="ml-auto" tone={wineApiEnabled ? 'gold' : 'mist'}>{wineApiEnabled ? 'aktiv' : 'av'}</Badge>
      </Card>
      <Card className="flex min-w-0 items-center gap-3 p-4">
        <Icon name="container" className="text-candle" />
        <div className="grid min-w-0">
          <span className="text-sm text-parchment">LWINdatabase.xlsx</span>
          <span className="truncate text-xs text-mist">
            {lwinExists ? (lwinTotalRows === undefined ? 'funnet, ikke lest ennå' : `${lwinTotalRows} rader`) : lwinPath ?? 'ikke konfigurert'}
          </span>
        </div>
        <Badge className="ml-auto" tone={lwinExists ? 'gold' : 'mist'}>{lwinExists ? 'funnet' : 'mangler'}</Badge>
      </Card>
    </div>
  );
}

function CandidateRow({ candidate }: { candidate: CatalogSyncCandidate }) {
  return (
    <li className="grid gap-2 rounded-md border border-line-soft p-3 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Icon name={candidate.category === 'spirits' ? 'spirits' : candidate.category === 'beer' ? 'beer' : 'bottle'} size={14} className="text-mist" />
          <span className="truncate text-sm text-parchment">{candidate.query}</span>
        </div>
        <div className="mt-1 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-mist">
          <span>{candidate.reason}</span>
          <span>{candidate.sourceHint}</span>
          <span>prio {candidate.priority}</span>
        </div>
      </div>
      <Badge tone={candidate.category === 'spirits' ? 'ruby' : 'gold'}>{candidate.category}</Badge>
    </li>
  );
}

function toggleCategory(
  category: BottleCategory,
  current: BottleCategory[],
  setCategories: (categories: BottleCategory[]) => void,
) {
  if (current.includes(category)) {
    const next = current.filter((item) => item !== category);
    setCategories(next.length ? next : current);
  } else {
    setCategories([...current, category]);
  }
}
